import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Heart, Settings, ChevronDown, ChevronUp, MessageCircle,
  Send, UserPlus, AlertCircle, Trash2,
} from 'lucide-react';

import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import CareProfileBanner from '../components/assessments/CareProfileBanner';
import BookOnboardingCard from '../components/dashboard/BookOnboardingCard';
import InviteFriendsDialog from '../components/circle/InviteFriendsDialog';
import { TaskCard } from '../components/circle/TaskCard';
import { CreateCircleDialog } from '../components/circle/CreateCircleDialog';
import { JoinCircleDialog } from '../components/circle/JoinCircleDialog';
import SupportLevelDialog from '../components/circle/SupportLevelDialog';
import { AddTaskDialog } from '../components/circle/AddTaskDialog';
import { CircleInfoDialog } from '../components/circle/CircleInfoDialog';
import TaskClaimedSheet from '../components/circle/TaskClaimedSheet';
import { CARE_PREFERENCES } from '../components/circle/carePreferences';
import { getCat } from '../components/circle/taskCategories';

const CircleHome = () => {
  const { user, logout } = useContext(AuthContext);

  const [circles, setCircles] = useState([]);
  const [activeCircle, setActiveCircle] = useState(null);
  const [todayData, setTodayData] = useState({ open: [], claimed: [], completed_today: [], overdue: [] });
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [showCreateCircle, setShowCreateCircle] = useState(false);
  const [showJoinCircle, setShowJoinCircle] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showCircleInfo, setShowCircleInfo] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [supportLevelPrompt, setSupportLevelPrompt] = useState(null); // { circleId, circleName } | null
  const [mySupportLevel, setMySupportLevel] = useState('active');
  const [claimedTask, setClaimedTask] = useState(null); // task obj → shows the "You're on it" sheet

  // Form state
  const [circleName, setCircleName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [taskForm, setTaskForm] = useState({ title: '', description: '', category: 'other', due_date: '', location: '', address: '', meal_info: '', meal_train_link: '', pickup: '', dropoff: '', round_trip: false });
  const [updateText, setUpdateText] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [guidance, setGuidance] = useState({ support_this_week: '', please_dont: '' });
  const [editingGuidance, setEditingGuidance] = useState(false);
  const [guidanceDraft, setGuidanceDraft] = useState({ support_this_week: '', please_dont: '' });

  const isPatient = activeCircle?.my_role === 'patient';

  useEffect(() => { fetchCircles(); }, []);

  const fetchCircles = async () => {
    try {
      const res = await axios.get(`${API}/circles`);
      setCircles(res.data);
      if (res.data.length > 0) {
        const c = res.data[0];
        setActiveCircle(c);
        fetchTodayData(c.id);
      } else {
        setLoading(false);
      }
    } catch (e) {
      console.error('Failed to fetch circles:', e);
      setLoading(false);
    }
  };

  const fetchTodayData = async (circleId) => {
    setLoading(true);
    try {
      const [todayRes, updatesRes, guidanceRes, membersRes] = await Promise.all([
        axios.get(`${API}/circles/${circleId}/today`).catch(() => ({ data: { open: [], claimed: [], completed_today: [], overdue: [] } })),
        axios.get(`${API}/circles/${circleId}/updates`),
        axios.get(`${API}/circles/${circleId}/guidance`).catch(() => ({ data: {} })),
        axios.get(`${API}/circles/${circleId}/members`).catch(() => ({ data: [] })),
      ]);
      setTodayData(todayRes.data);
      setUpdates(updatesRes.data.slice(0, 10));
      const g = guidanceRes.data || {};
      setGuidance(g);
      setGuidanceDraft({ support_this_week: g.support_this_week || '', please_dont: g.please_dont || '' });
      // Community members get an updates-only view (they see the same members list
      // as everyone else but don't see tasks / guidance / onboarding CTAs).
      const me = (membersRes.data || []).find((m) => (m.user?.id || m.user_id) === user?.id);
      setMySupportLevel(me?.support_level || 'active');
    } catch (e) {
      console.error('Failed to fetch today data:', e);
    }
    setLoading(false);
  };

  const handleCreateCircle = async () => {
    if (!circleName.trim()) return;
    try {
      const res = await axios.post(`${API}/circles`, { name: circleName });
      toast.success(`Circle created! Invite code: ${res.data.invite_code}`);
      setCircleName('');
      setShowCreateCircle(false);
      fetchCircles();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating circle'); }
  };

  const handleJoinCircle = async () => {
    if (!joinCode.trim()) return;
    try {
      const res = await axios.post(`${API}/circles/join`, { invite_code: joinCode.trim() });
      toast.success(res.data.message || 'Joined circle!');
      setJoinCode('');
      setShowJoinCircle(false);
      // Immediately prompt the new member to declare their support level.
      const joined = res.data.circle;
      if (joined?.id) setSupportLevelPrompt({ circleId: joined.id, circleName: joined.name });
      fetchCircles();
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
  };

  const handleCreateTask = async () => {
    if (!taskForm.title.trim() || !activeCircle) return;
    try {
      await axios.post(`${API}/circles/${activeCircle.id}/tasks`, taskForm);
      toast.success('Task posted!');
      setTaskForm({ title: '', description: '', category: 'other', due_date: '', location: '', address: '', meal_info: '', meal_train_link: '', pickup: '', dropoff: '', round_trip: false });
      setShowAddTask(false);
      fetchTodayData(activeCircle.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating task'); }
  };

  const handleClaimTask = async (task) => {
    try {
      await axios.post(`${API}/tasks/${task.id}/claim`);
      toast.success('You claimed this task!');
      setClaimedTask(task);
      fetchTodayData(activeCircle.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not claim'); }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/complete`);
      toast.success('Task completed!');
      fetchTodayData(activeCircle.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not complete'); }
  };

  const handleUnclaimTask = async (taskId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/unclaim`);
      toast.success('Task unclaimed');
      fetchTodayData(activeCircle.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not unclaim'); }
  };

  const handleSnoozeTask = async (taskId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/snooze`);
      toast.success("Snoozed for 24 hours — we'll auto-release it if you can't get to it.");
      fetchTodayData(activeCircle.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not snooze'); }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success('Task deleted');
      fetchTodayData(activeCircle.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };

  const handleDeleteUpdate = async (updateId) => {
    try {
      await axios.delete(`${API}/updates/${updateId}`);
      toast.success('Update deleted');
      fetchTodayData(activeCircle.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };

  const handleSaveGuidance = async () => {
    try {
      await axios.put(`${API}/circles/${activeCircle.id}/guidance`, guidanceDraft);
      setGuidance(guidanceDraft);
      setEditingGuidance(false);
      toast.success('Preferences updated!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  const handlePostUpdate = async () => {
    if (!updateText.trim() || !activeCircle) return;
    try {
      await axios.post(`${API}/circles/${activeCircle.id}/updates`, { message: updateText });
      toast.success('Update posted!');
      setUpdateText('');
      fetchTodayData(activeCircle.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error posting update'); }
  };

  // ===== NO CIRCLE STATE =====
  if (!loading && circles.length === 0) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6" data-testid="no-circle">
        <div className="max-w-sm w-full text-center space-y-6">
          <div className="relative w-16 h-16 flex items-center justify-center mx-auto">
            <div className="absolute inset-0 rounded-full border border-cyan-300/40" />
            <div className="absolute inset-2 rounded-full border border-cyan-400/60" />
            <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center">
              <Heart className="w-3.5 h-3.5 text-white" fill="currentColor" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-sm text-muted-foreground">Start by creating your support circle or joining one.</p>
          <div className="space-y-3">
            <Button onClick={() => setShowCreateCircle(true)} className="w-full h-12 rounded-xl bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-700 hover:to-emerald-700 text-white font-semibold text-base" data-testid="create-circle-btn">
              <Plus className="w-4 h-4 mr-2" /> Create My Circle
            </Button>
            <Button onClick={() => setShowJoinCircle(true)} variant="outline" className="w-full h-12 rounded-xl text-base" data-testid="join-circle-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Join a Circle
            </Button>
          </div>
        </div>
        <CreateCircleDialog open={showCreateCircle} onOpenChange={setShowCreateCircle} name={circleName} setName={setCircleName} onSubmit={handleCreateCircle} />
        <JoinCircleDialog open={showJoinCircle} onOpenChange={setShowJoinCircle} code={joinCode} setCode={setJoinCode} onSubmit={handleJoinCircle} />
        <SupportLevelDialog
          open={!!supportLevelPrompt}
          onOpenChange={(v) => { if (!v) setSupportLevelPrompt(null); }}
          circleId={supportLevelPrompt?.circleId}
          circleName={supportLevelPrompt?.circleName}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-200 border-t-cyan-600"></div>
      </div>
    );
  }

  const { open, claimed, completed_today, overdue } = todayData;

  // ===== MAIN TODAY VIEW =====
  return (
    <div className="min-h-screen bg-background pb-24" data-testid="circle-home">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border/50 px-5 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold text-foreground truncate" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="circle-name-header">{activeCircle?.name}</h1>
            <p className="text-[11px] text-muted-foreground">{activeCircle?.member_count || 0} member{(activeCircle?.member_count || 0) !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {activeCircle?.invite_code && (
              <button
                onClick={() => setShowInviteDialog(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold shadow-sm transition-colors"
                data-testid="header-invite-btn"
              >
                <UserPlus className="w-3.5 h-3.5" /> Invite
              </button>
            )}
            <button onClick={() => setShowCircleInfo(true)} className="p-2 text-muted-foreground hover:text-foreground transition-colors" data-testid="circle-info-btn">
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-5 pt-4 space-y-5">
        {mySupportLevel === 'community' && (
          <section className="rounded-2xl p-5 bg-gradient-to-br from-cyan-50 to-emerald-50 dark:from-cyan-950/40 dark:to-emerald-950/40 border border-cyan-100 dark:border-cyan-900/40" data-testid="community-view-banner">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-cyan-700 dark:text-cyan-300">Community Support</p>
            <h2 className="text-base font-bold text-foreground mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
              You{"'"}re part of {activeCircle?.name}
            </h2>
            <p className="text-xs text-muted-foreground leading-relaxed mt-1">
              You{"'"}ll see updates from the circle here. If your capacity changes and you{"'"}d like to help with hands-on tasks, tap your name in Circle Settings to switch to Active Support.
            </p>
          </section>
        )}
        {mySupportLevel !== 'community' && (<>

        <CareProfileBanner />

        {/* Daily prompt — what do you need today? */}
        <button
          onClick={() => setShowAddTask(true)}
          className="w-full surface-card rounded-2xl p-4 text-left hover:border-cyan-300 transition-all group"
          data-testid="add-task-btn"
        >
          <p className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
            What do you need support on today?
          </p>
          <p className="text-xs text-muted-foreground mt-1">Post a task and your circle can claim it.</p>
          <span className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-cyan-600 group-hover:gap-2 transition-all">
            <Plus className="w-3.5 h-3.5" /> Post a task
          </span>
        </button>

        {/* My Care Preferences */}
        <section className="surface-card rounded-2xl p-4" data-testid="guidance-box">
          {editingGuidance ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-foreground block mb-1">My care preferences</label>
                <p className="text-[11px] text-muted-foreground mb-2">Tap any that fit — or write your own below.</p>
                {(() => {
                  const text = guidanceDraft.support_this_week || '';
                  const isSelected = (p) => text.includes(p.label);
                  const toggle = (p) => {
                    if (isSelected(p)) {
                      const next = text
                        .replace(new RegExp(`\\s*${p.label.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}\\s*`), ' ')
                        .replace(/\s{2,}/g, ' ')
                        .replace(/^[\s,]+|[\s,]+$/g, '')
                        .trim();
                      setGuidanceDraft({ ...guidanceDraft, support_this_week: next });
                    } else {
                      const sep = text.trim() ? (text.trim().endsWith('.') ? ' ' : '. ') : '';
                      setGuidanceDraft({ ...guidanceDraft, support_this_week: `${text.trim()}${sep}${p.label}`.trim() });
                    }
                  };
                  return (
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="preference-chips">
                      {CARE_PREFERENCES.map((p) => (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => toggle(p)}
                          className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                            isSelected(p)
                              ? 'bg-cyan-600 text-white border-cyan-600'
                              : 'bg-background text-foreground border-border/60 hover:border-cyan-400 hover:bg-cyan-50/50 dark:hover:bg-cyan-900/10'
                          }`}
                          data-testid={`preference-chip-${p.label.slice(0, 12).replace(/\s/g, '-').toLowerCase()}`}
                        >
                          <span aria-hidden="true">{p.emoji}</span>
                          <span>{p.label}</span>
                        </button>
                      ))}
                    </div>
                  );
                })()}
                <Textarea
                  value={guidanceDraft.support_this_week}
                  onChange={(e) => setGuidanceDraft({ ...guidanceDraft, support_this_week: e.target.value })}
                  placeholder="Or write your own — e.g., I have chemo Tues + Thurs. Rides and meals those days would be amazing."
                  className="rounded-xl resize-none"
                  rows={3}
                  data-testid="guidance-support-input"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveGuidance} size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl text-xs" data-testid="guidance-save">Save</Button>
                <Button onClick={() => { setEditingGuidance(false); setGuidanceDraft(guidance); }} size="sm" variant="ghost" className="rounded-xl text-xs">Cancel</Button>
              </div>
            </div>
          ) : (
            <div>
              {guidance.support_this_week || guidance.please_dont ? (
                <div className="space-y-2.5">
                  {guidance.support_this_week && (
                    <div>
                      <p className="text-[10px] font-semibold text-cyan-600 uppercase tracking-wider mb-0.5">My care preferences</p>
                      <p className="text-sm text-foreground leading-relaxed">{guidance.support_this_week}</p>
                    </div>
                  )}
                  {guidance.please_dont && (
                    <div>
                      <p className="text-[10px] font-semibold text-rose-500 uppercase tracking-wider mb-0.5">Please don{"'"}t</p>
                      <p className="text-sm text-foreground leading-relaxed">{guidance.please_dont}</p>
                    </div>
                  )}
                  {isPatient && (
                    <button onClick={() => setEditingGuidance(true)} className="text-xs text-cyan-600 font-medium hover:text-cyan-700 transition-colors" data-testid="guidance-edit">
                      Edit preferences
                    </button>
                  )}
                </div>
              ) : (
                isPatient ? (
                  <button onClick={() => setEditingGuidance(true)} className="w-full text-left" data-testid="guidance-edit">
                    <p className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      My care preferences
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">Let your squad know how you want to be supported — pick a few or write your own.</p>
                    <span className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-cyan-600">
                      <Plus className="w-3.5 h-3.5" /> Set preferences
                    </span>
                  </button>
                ) : (
                  <div>
                    <p className="text-xs text-muted-foreground italic">No care preferences set yet — the patient will share how they want to be supported when they{"'"}re ready.</p>
                  </div>
                )
              )}
            </div>
          )}
        </section>

        {/* Book Your Onboarding Session — dedicated card in the middle of the page */}
        <BookOnboardingCard />

        {/* Overdue */}
        {overdue.length > 0 && (
          <section data-testid="overdue-tasks">
            <p className="text-xs font-semibold text-rose-600 uppercase tracking-wider mb-2 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> Overdue ({overdue.length})
            </p>
            <div className="space-y-2">
              {overdue.map((task) => (
                <TaskCard key={task.id} task={task} userId={user?.id} onClaim={handleClaimTask} onComplete={handleCompleteTask} onUnclaim={handleUnclaimTask} onSnooze={handleSnoozeTask} onDelete={handleDeleteTask} isOverdue />
              ))}
            </div>
          </section>
        )}

        {/* Open Tasks */}
        <section data-testid="open-tasks">
          <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">
            Needs help ({open.length})
          </p>
          {open.length === 0 ? (
            <div className="surface-card rounded-xl p-6 text-center">
              <p className="text-sm text-muted-foreground">No open tasks right now.</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Post a task to get support from your circle.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {open.map((task) => (
                <TaskCard key={task.id} task={task} userId={user?.id} onClaim={handleClaimTask} onComplete={handleCompleteTask} onUnclaim={handleUnclaimTask} onSnooze={handleSnoozeTask} onDelete={handleDeleteTask} />
              ))}
            </div>
          )}
        </section>

        {/* Claimed Tasks */}
        {claimed.length > 0 && (
          <section data-testid="claimed-tasks">
            <p className="text-xs font-semibold text-cyan-600 uppercase tracking-wider mb-2">
              In progress ({claimed.length})
            </p>
            <div className="space-y-2">
              {claimed.map((task) => (
                <TaskCard key={task.id} task={task} userId={user?.id} onClaim={handleClaimTask} onComplete={handleCompleteTask} onUnclaim={handleUnclaimTask} onSnooze={handleSnoozeTask} onDelete={handleDeleteTask} />
              ))}
            </div>
          </section>
        )}

        {/* Completed Today */}
        {completed_today.length > 0 && (
          <section data-testid="completed-tasks">
            <button
              onClick={() => setShowCompleted(!showCompleted)}
              className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2"
              data-testid="toggle-completed"
            >
              Done today ({completed_today.length}) {showCompleted ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {showCompleted && (
              <div className="space-y-2">
                {completed_today.map((task) => (
                  <div key={task.id} className="surface-card rounded-xl p-3 opacity-60" data-testid={`completed-task-${task.id}`}>
                    <p className="text-sm text-foreground line-through">{task.title}</p>
                    <p className="text-[11px] text-muted-foreground">{getCat(task.category).label} - {task.claimed_by_name || task.creator_name}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        </>)}

        {/* Updates */}
        <section data-testid="updates-section">
          <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
            <MessageCircle className="w-3.5 h-3.5" /> Updates
          </p>
          <div className="surface-card rounded-xl p-4 space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="Share an update with your circle..."
                value={updateText}
                onChange={(e) => setUpdateText(e.target.value)}
                className="rounded-xl flex-1"
                data-testid="update-input"
                onKeyDown={(e) => { if (e.key === 'Enter') handlePostUpdate(); }}
              />
              <Button
                onClick={handlePostUpdate}
                disabled={!updateText.trim()}
                size="sm"
                className="rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white px-3"
                data-testid="send-update-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            {updates.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {updates.map((u) => (
                  <div key={u.id} className="py-2 border-b border-border/30 last:border-0" data-testid={`update-${u.id}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-primary/15 rounded-full flex items-center justify-center text-cyan-600 text-[10px] font-bold flex-shrink-0">
                        {u.author_name?.charAt(0)}
                      </div>
                      <span className="text-xs font-semibold text-foreground">{u.author_name}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      {(u.author_id === user?.id || isPatient) && (
                        <button onClick={() => handleDeleteUpdate(u.id)} className="ml-auto text-muted-foreground/30 hover:text-rose-500 transition-colors" data-testid={`delete-update-${u.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 pl-8">{u.message}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center py-2">No updates yet</p>
            )}
          </div>
        </section>
      </div>

      {/* Dialogs */}
      <CreateCircleDialog open={showCreateCircle} onOpenChange={setShowCreateCircle} name={circleName} setName={setCircleName} onSubmit={handleCreateCircle} />
      <JoinCircleDialog open={showJoinCircle} onOpenChange={setShowJoinCircle} code={joinCode} setCode={setJoinCode} onSubmit={handleJoinCircle} />
      <AddTaskDialog open={showAddTask} onOpenChange={setShowAddTask} form={taskForm} setForm={setTaskForm} onSubmit={handleCreateTask} />
      <CircleInfoDialog open={showCircleInfo} onOpenChange={setShowCircleInfo} circle={activeCircle} onLogout={logout} userName={user?.name} currentUserId={user?.id} isPatient={isPatient} />
      <InviteFriendsDialog open={showInviteDialog} onOpenChange={setShowInviteDialog} circle={activeCircle} userName={user?.name} />
      <TaskClaimedSheet
        open={!!claimedTask}
        onOpenChange={(v) => { if (!v) setClaimedTask(null); }}
        task={claimedTask}
      />
      <SupportLevelDialog
        open={!!supportLevelPrompt}
        onOpenChange={(v) => { if (!v) setSupportLevelPrompt(null); }}
        circleId={supportLevelPrompt?.circleId}
        circleName={supportLevelPrompt?.circleName}
      />
    </div>
  );
};

export default CircleHome;
